import React, { useState } from 'react';
import { TransactionsTableContext, ITransactionsTableContext } from './TransactionsTableContext';
import { useAsyncData } from '@shared/react';
import { useErrorNotify } from '@features/useErrorNotify';
import { getLogo, getTransactions } from '../../api';
import { ISettingsTransactionsDto } from '@shared/api';
import { useDrawer } from '@features/Modal';
import { TransactionViewDrawer } from '../../components';
import { usePagination } from '@features/Pagination';
import { combineFilterWithOp, joinFiltersWithComma, prepareUniqueSymbols } from '@shared/utils';
import { combineFieldsFilter } from '@shared/utils/query/combineFilter/combineFilter';

export interface ITransactionDesc {
    id: boolean;
    accountId: boolean;
    orderId: boolean;
    date: boolean;
    price: boolean;
    settlementDate: boolean;
    quantity: boolean;
    type: boolean;
    execId: boolean;
    processed: boolean;
    [key: string]: boolean;
}

interface TransactionTableProviderProps {
    orderId: number;
}

export const TransactionsTableProvider: React.FC<React.PropsWithChildren<TransactionTableProviderProps>> = ({
    orderId,
    children,
}) => {
    const [transactionsIdentifier, setTransactionsIdentifier] = useState<string>('');
    const [cusip, setCusip] = useState<string>('');
    const [transactionTypes, setTransactionTypes] = useState<string[]>([]);
    const [dateRange, setDateRange] = useState<[string | null, string | null]>(['', '']);
    const [sortFieldName, setSortFieldName] = useState('date');
    const [desc, setDesc] = useState<ITransactionDesc>({
        id: true,
        accountId: true,
        orderId: true,
        date: true,
        price: true,
        settlementDate: true,
        quantity: true,
        type: true,
        execId: true,
        processed: true,
    });

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination();

    const {
        data: transactions,
        loading: transactionsLoading,
        error: transactionsError,
        handleRefresh: transactionRefresh,
    } = useAsyncData(
        async () => {
            const userStorage = localStorage.getItem('User');
            let userObject = null;
            let filterClientAccount = null;
            let filteredAccounts = null;
            let filterToAdd;
            userObject = JSON.parse(userStorage ? userStorage : '');
            if (userObject?.accounts) {
                if (userObject.userRole === 5) {
                    filterClientAccount = `AccountId=${userObject.accounts[0].id}`;
                    filterToAdd = filterClientAccount;
                } else {
                    const accountId = userObject.accounts?.[0]?.id || null; // Get the first account's ID or null
                    const subAccountIds = userObject.subAccounts?.map((sub: any) => sub.id) || []; // Get subAccount IDs or an empty array

                    const combinedAccounts =
                        subAccountIds.length > 0
                            ? [accountId, ...subAccountIds] // Include accountId and subAccountIds if subAccountIds is not empty
                            : [accountId]; // Only include accountId if subAccountIds is empty

                    // Filter out null values
                    filteredAccounts = combinedAccounts.filter((id) => id !== null);
                    filterToAdd = combineFilterWithOp({
                        AccountId: {
                            value: filteredAccounts.length && ` ${filteredAccounts.join(';')}`,
                            operator: ' #In',
                        },
                    });
                }
            }

            const filter = {
                type: { value: transactionTypes.length && ` ${transactionTypes.join(';')}`, operator: ' #In' },
                from: { keyOverride: 'Date', value: dateRange[0], operator: '>=' },
                to: { keyOverride: 'Date', value: dateRange[1], operator: '<' },
            };
            const filterKeys = {
                Ticker: { value: transactionsIdentifier.toUpperCase() },
                Cusip: { value: transactionsIdentifier.toUpperCase(), operator: '=*' },
                OrderId: { value: orderId },
            };
            const { items, totalResults } = await getTransactions({
                page: page,
                pageSize,
                filter: joinFiltersWithComma(
                    combineFilterWithOp(filter),
                    combineFieldsFilter(filterKeys),
                    filterToAdd ? filterToAdd : '',
                ),
                sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
            });
            setTotalCount(totalResults);
            return items || [];
        },
        [],
        [pageSize, page, transactionsIdentifier, cusip, dateRange, transactionTypes],
    );

    const { data: arrayWithLogos, loading: logoLoading } = useAsyncData(
        async () => {
            return await Promise.all(
                prepareUniqueSymbols(transactions, 'symbol').map(async (item) => {
                    const res = await getLogo({ symbol: item.symbol as string });
                    return { link: res, symbol: item.symbol as string };
                }),
            );
        },
        [],
        [transactions],
    );

    const handleTransactionsSort = (fieldName: string) => {
        setSortFieldName(fieldName);
        setDesc((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
        transactionRefresh();
    };

    useErrorNotify(transactionsError, 'Records');

    const { open: openTransactionsViewDrawer } = useDrawer(TransactionViewDrawer);

    const onTransactionViewClick = (transaction: ISettingsTransactionsDto) => {
        openTransactionsViewDrawer({ transaction });
    };

    const value: ITransactionsTableContext = {
        dateRange,
        transactions,
        transactionsLoading,
        arrayWithLogos,
        logoLoading,
        paginationOptions,
        sortFieldName,
        desc,
        handleTransactionsSort,
        transactionTypes,
        onTransactionViewClick,
        setTransactionsIdentifier,
        setCusip,
        setDateRange,
        setTransactionTypes,
    };

    return <TransactionsTableContext.Provider value={value}>{children}</TransactionsTableContext.Provider>;
};
